import React from "react";
import { DatePicker } from "./DatePicker";
import { Select } from "./Select";
import { cn } from "../../../lib/cn";

/**
 * PeriodFilter — selector de periodo con presets y rango personalizado.
 *
 * @example
 * const [period, setPeriod] = useState({ preset: 'all', from: null, to: null });
 * <PeriodFilter value={period} onChange={setPeriod} />
 *
 * Forma del value:
 *   { preset: 'all' | 'month-current' | ... | 'custom',
 *     from: 'YYYY-MM-DD' | null,
 *     to: 'YYYY-MM-DD' | null }
 */

// Genera los rangos para cada preset, basados en la fecha actual
function computeRange(preset) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const toIso = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${dd}`;
    };

    switch (preset) {
        case 'all':
            return { from: null, to: null };

        case 'month-current': {
            const from = new Date(today.getFullYear(), today.getMonth(), 1);
            return { from: toIso(from), to: toIso(today) };
        }

        case 'month-prev': {
            const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const to = new Date(today.getFullYear(), today.getMonth(), 0); // último día del mes anterior
            return { from: toIso(from), to: toIso(to) };
        }

        case 'last-7d': {
            const from = new Date(today);
            from.setDate(from.getDate() - 6);
            return { from: toIso(from), to: toIso(today) };
        }

        case 'last-30d': {
            const from = new Date(today);
            from.setDate(from.getDate() - 29);
            return { from: toIso(from), to: toIso(today) };
        }

        case 'last-90d': {
            const from = new Date(today);
            from.setDate(from.getDate() - 89);
            return { from: toIso(from), to: toIso(today) };
        }

        case 'quarter-current': {
            const q = Math.floor(today.getMonth() / 3);
            const from = new Date(today.getFullYear(), q * 3, 1);
            return { from: toIso(from), to: toIso(today) };
        }

        case 'year-current': {
            const from = new Date(today.getFullYear(), 0, 1);
            return { from: toIso(from), to: toIso(today) };
        }

        default:
            return { from: null, to: null };
    }
}

const PRESETS = [
    { value: 'all', label: 'Todo el periodo' },
    { value: 'month-current', label: 'Mes actual' },
    { value: 'month-prev', label: 'Mes anterior' },
    { value: 'last-7d', label: 'Últimos 7 días' },
    { value: 'last-30d', label: 'Últimos 30 días' },
    { value: 'last-90d', label: 'Últimos 90 días' },
    { value: 'quarter-current', label: 'Trimestre actual' },
    { value: 'year-current', label: 'Año actual' },
    { value: 'custom', label: 'Personalizado…' },
];

// Helpers para convertir entre Date object y string ISO
const isoToDate = (iso) => {
    if (!iso) return undefined;
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
};
const dateToIso = (d) => {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
};

export const PeriodFilter = ({ value, onChange, className }) => {
    const period = value || { preset: 'all', from: null, to: null };

    const handlePresetChange = (e) => {
        const newPreset = e.target.value;
        if (newPreset === 'custom') {
            // Solo cambiar el preset, las fechas las pondrá el DatePicker
            onChange({ preset: 'custom', from: period.from, to: period.to });
        } else {
            const range = computeRange(newPreset);
            onChange({ preset: newPreset, ...range });
        }
    };

    const handleRangeChange = (range) => {
        onChange({
            preset: 'custom',
            from: dateToIso(range.from),
            to: dateToIso(range.to),
        });
    };

    return (
        <div className={cn("flex items-center gap-2", className)}>
            <Select value={period.preset} onChange={handlePresetChange}>
                {PRESETS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                ))}
            </Select>
            {period.preset === 'custom' && (
                <DatePicker
                    value={{
                        from: isoToDate(period.from),
                        to: isoToDate(period.to),
                    }}
                    onChange={handleRangeChange}
                    placeholder="Elegir fechas"
                />
            )}
        </div>
    );
};
